import {faEllipsisVertical} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Button from 'Components/Atoms/Button/Button';
import Container from 'Components/Atoms/Container/Container';
import Dropdown from 'Components/Atoms/Dropdown/Dropdown';
import DropdownItem from 'Components/Atoms/Dropdown/DropdownItem/DropdownItem';
import EntityIcon from 'Components/Atoms/EntityIcon/EntityIcon';
import StatusLabel from 'Components/Atoms/Label/StatusLabel/StatusLabel';
import Title from 'Components/Atoms/Title/Title';
import ButtonGroup from 'Components/Molecules/ButtonGroup/ButtonGroup';
import DisplayName from 'Components/Molecules/DisplayName/DisplayName';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import Modal from 'Components/Molecules/Modals/Modal/Modal';
import ModalFooter from 'Components/Molecules/Modals/ModalFooter/ModalFooter';
import ModalHeader from 'Components/Molecules/Modals/ModalHeader/ModalHeader';
import Tabs from 'Components/Molecules/Tabs/Tabs';
import ContactHeader from 'Components/Organisms/Contact/ContactHeader/ContactHeader';
import Field from 'Pages/Dashboard/Settings/FieldsManagement/Fields/FieldTypes/Field';
import {useEffect, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import ModuleHelper, {FILE_GENERATOR_TEMPLATES, MODULES} from 'Services/Settings/Modules/Helper';
import {STATUS} from 'Services/Status/Status.types';
import {SUBSTATUS} from 'Services/Status/Substatus.types';
import {Tab, useTab} from 'Services/Tab/TabContext';
import {updateBusinessUnit, updateBusinessUnitValues} from 'Store/BusinessUnit/Actions';
import {updateCandidate, updateCandidateValues} from 'Store/Candidate/Actions';
import {updateContact, updateContactValues} from 'Store/Contact/Actions';
import {FieldType} from 'Store/FieldManagement/PropTypes';
import {fetchContactModules} from 'Store/Modules/ContactModule/Actions';
import {StoreState} from 'Store/Reducers';
import {getEntityById} from 'Store/Settings/Entities/Selectors';
import {fetchEntityTypesByEntityId} from 'Store/Settings/EntityTypes/Actions';
import {getEntityTypeById, getEntityTypesByEntityId} from 'Store/Settings/EntityTypes/Selector';
import {getFieldsById} from 'Store/Settings/Fields/Selectors';
import {fetchModules} from 'Store/Settings/Modules/Actions';
import {getRecordById, getValuesByEntityNameByEntityId, isRecordPatching} from 'Store/Shared/Store/Values/Selector';
import {updateVacancy, updateVacancyValues} from 'Store/Vacancy/Actions';
import styles from './DetailedHeader.module.sass';
import useModuleTotals from 'Api/Modules/Totals/useModuleTotals';
import DeletionModal from 'Components/Molecules/Modals/Actions/DeletionModal/DeletionModal';
import usePermissions from 'Services/Authentication/usePermissions';
import Permissions from 'Services/Authentication/Permissions/Permissions.types';
import {useEntityRecord} from 'Services/Api/useEntityRecord/useEntityRecord';
import useFetch from 'Api/useFetch';
import {updateOffer, updateOfferValues} from 'Store/Offer/Actions';

const DetailedHeader = ({
                            record_id,
                            entity, record,
                            entityType, entityTypes,
                            fields, values,
                            isPatching,
                            ...props
                        }: DetailedHeaderProps) => {
    const navigate = useNavigate();
    const { isDeleted, isDeleting, deleteRecordById } = useEntityRecord(entity?.name);
    const permissions = usePermissions();
    const updateRecord = useMemo(() => EntityHelper.update(props, entity.name), [entity?.name]);

    const { data: tabTotals } = useModuleTotals(entity?.id, record_id);

    const { state: { active }, setTab } = useTab();
    const [state, setState] = useState({
        isEdit: false,
        showDeleteConfirmation: false,
        documentTitle: ''
    });

    const [data, setData] = useState<any>({
        tabs: [],
        type_id: record?.type_id,
        status: record?.status?.status,
        substatus: record?.status?.substatus
    });

    useEffect(() => {
        setData({
            type_id: record?.type_id,
            status: record?.status?.status,
            substatus: record?.status?.substatus
        });
    }, [record]);

    const { data: record_data, isLoading } = useFetch(
        `/api/v2.9/${EntityHelper.getEndpoint(entity.name)}/${record.id}`,
        {},
        {
            select: (data: any) => data?.data,
            staleTime: 1000 * 60 * 5
        }
    )

    const tabs = useMemo(() => {
        console.log('RECORD DATA: ', record_data);
        let tabs: any[] = [{
            id: Tab.OVERVIEW,
            title: 'Overzicht'
        }];

        if (!entity) return tabs;
        const { modules } = entity;
        if (!modules) return tabs;

        if (!!modules?.contact) {
            let title = ModuleHelper.getModuleDisplayValue(MODULES.CONTACT);
            const total = tabTotals?.['contact_details'];
            if (total) title += ` (${total})`;

            tabs.push({
                id: Tab.CONTACT,
                title: title
            });
        }

        if (!!record_data?.option_lists) {
            let title = 'Optielijsten';
            if (record_data?.option_lists?.length > 0) title += ` (${record_data?.option_lists?.length})`;
            tabs.push({
                id: Tab.OPTION_LISTS,
                title: title
            });
        }

        if (!!record_data?.mails) {
            let title = 'Mails';
            if (record_data?.mails?.length > 0) title += ` (${record_data?.mails?.length})`;
            tabs.push({
                id: Tab.MAILS,
                title: title
            });
        }

        if (!!modules?.file_generator?.talent_proposal) {
            let title = ModuleHelper.getModuleDisplayValue(MODULES.FILE_GENERATOR, FILE_GENERATOR_TEMPLATES.TALENT_PROPOSAL);
            const total = tabTotals?.['file_generator']?.['talent_proposal'];
            if (total) title += ` (${total})`;

            tabs.push({
                id: Tab.TALENT_PROPOSAL,
                title: title
            });
        }

        if (!!modules?.file_generator?.cooperation_conditions) {
            let title = ModuleHelper.getModuleDisplayValue(MODULES.FILE_GENERATOR, FILE_GENERATOR_TEMPLATES.COOPERATION_CONDITIONS);
            const total = tabTotals?.['file_generator']?.['cooperation_conditions'];
            if (total) title += ` (${total})`;

            tabs.push({
                id: Tab.COOPERATION_CONDITION,
                title: title
            });
        }

        if (!!modules?.files) {
            let title = ModuleHelper.getModuleDisplayValue(MODULES.FILES);
            const total = tabTotals?.['file_management'];
            if (total) title += ` (${total})`;

            tabs.push({
                id: Tab.FILES,
                title: title
            });
        }

        if (!!modules?.notes) {
            let title = ModuleHelper.getModuleDisplayValue(MODULES.NOTES);
            const total = tabTotals?.['notes'];
            if (total) title += ` (${total})`;

            tabs.push({
                id: Tab.NOTES,
                title: title
            });
        }

        return tabs;
    }, [record_data, tabTotals]);

    useEffect(() => {
        if (isDeleted) navigate(EntityHelper.getEntityOverviewEndpointByEntityName(entity.name));
    }, [isDeleted]);

    const handleEdit = (value: any) => () => setState((state) => ({
        ...state,
        isEdit: value
    }));

    const onClose = () => setState(() => ({
        ...state,
        isEdit: false
    }));

    const handleSave = () => {
        const recordKeys = Object.keys(data);
        if (recordKeys.length > 0) {
            updateRecord(record_id, data);
        }
    };

    const handleChange = (key: string) => (value: any) => setData((data) => ({
        ...data,
        [key]: value
    }));

    const handleDelete = () => {
        setState((state) => ({
            ...state,
            showDeleteConfirmation: !state.showDeleteConfirmation
        }));
    }

    const handleStatusChange = (value) => {
        setData((data) => ({
            ...data,
            status: STATUS.getStatusBySubStatus(value),
            substatus: value
        }));
    }

    const processDelete = () => deleteRecordById(record_id);

    useEffect(() => {
        if (entity?.id) {
            props.fetchModules();
            props.fetchContactModules(record_id);
            props.fetchEntityTypesByEntityId(entity?.id);
        }
    }, [entity?.id]);

    useEffect(() => {
        if (!isPatching && Object.keys(data)?.length > 0) {
            setState((state) => ({
                ...state,
                isEdit: false
            }));
        }
    }, [isPatching]);

    useEffect(() => {
        setState((state) => ({
            ...state,
            documentTitle: `${record?.display_name} - ${EntityHelper.getDisplayValueByName(entity?.name)} - Growing People App`
        }));
    }, []);

    return <div className={styles['detailedheader']}>
        <Helmet>
            <title>{ state.documentTitle }</title>
        </Helmet>
        {
            state.showDeleteConfirmation
            && <DeletionModal
                onClose={handleDelete}
                onDelete={processDelete}
                isDeleting={isDeleting}
            />
        }
        <div className={styles['detailedheader__content']}>
            <div className={styles['detailedheader__content-title']}>
                <div className={styles['deatiledheader__content-title-icon']}>
                    <EntityIcon
                        entity={entity?.name}
                        size='xlarge'
                    />
                </div>
                <div className={styles['detailedheader__content-title-info']}>
                    <div className={styles['detailedheader__content-title-info-header']}>
                        <div className={styles['detailedheader__content-title-info-header-truncate']}>
                            { record?.display_name }
                        </div>
                        <div className={styles['detailedheader__content-title-info-header-fixed']}>
                            { entityType?.name && <>Type: {entityType?.name}</> }
                        </div>
                    </div>
                    <StatusLabel
                        status={record?.status?.status}
                        substatus={record?.status?.substatus}
                    />
                </div>
                <div className={styles['detailedheader__content-title-toolbar']}>
                    <Dropdown
                        position='right'
                        trigger={
                            <Button
                                size='large'
                                shape='circle'
                                color='neutral-dark'
                                iconBefore={<FontAwesomeIcon icon={faEllipsisVertical} />}
                            />
                        }
                    >
                        <DropdownItem onClick={handleEdit(true)}>Aanpassen</DropdownItem>
                        {
                            permissions?.includes(Permissions.Candidate.DELETE) &&
                            <DropdownItem
                                border='top'
                                state='danger'
                                onClick={handleDelete}
                            >
                                Verwijderen
                            </DropdownItem>
                        }
                    </Dropdown>
                </div>
            </div>
            <ContactHeader
                record_id={record_id}
                entity_id={entity.id}
            />
        </div>

        {
            state.isEdit && <Modal size='small'>
                <ModalHeader size='small'>
                    <Title
                        size='medium'
                        weight='bold'
                    >
                        Pas de { EntityHelper.getSingularDisplayValueByName(entity?.name)?.toLowerCase() } aan
                    </Title>
                </ModalHeader>

                <Container padding='vertical'>
                    <FormGroup
                        title='Type'
                        isRequired
                    >
                        <Field
                            isEdit={true}
                            value={data?.type_id ?? record?.type_id}
                            onChange={handleChange('type_id')}
                            type={FieldType.Select}
                            fieldProperties={{
                                list: entityTypes?.map(i => ({
                                    label: i?.name,
                                    value: i?.id
                                }))
                            }}
                        />
                    </FormGroup>
                    <FormGroup
                        title='Status'
                        isRequired
                    >
                        <Field
                            isEdit={true}
                            value={data?.substatus ?? record?.status?.substatus}
                            onChange={(value: any) => handleStatusChange(value)}
                            type={FieldType.Select}
                            fieldProperties={{
                                list: [
                                    ...SUBSTATUS.getDropdownList(SUBSTATUS.getListByEntityNameByStatus(entity?.name, 'ACTIVE')),
                                    ...SUBSTATUS.getDropdownList(SUBSTATUS.getListByEntityNameByStatus(entity?.name, 'PASSIVE')),
                                    ...SUBSTATUS.getDropdownList(SUBSTATUS.getListByEntityNameByStatus(entity?.name, 'INACTIVE'))
                                ]
                            }}
                        />
                    </FormGroup>
                </Container>

                <ModalFooter size='small'>
                    <ButtonGroup>
                        { !isPatching &&
                            <Button
                                variant="transparent"
                                onClick={onClose}
                            >
                                Annuleren
                            </Button>
                        }
                        <Button
                            color="primary"
                            variant="solid"
                            onClick={handleSave}
                            isLoading={isPatching}
                            isDisabled={!data?.type_id || !data?.status || !data?.substatus}
                        >
                            Aanpassen
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>
        }

        {
            !isLoading &&
            <Tabs
                data={tabs}
                active={active}
                onChange={(value) => setTab(value)}
            />
        }
    </div>
}

interface Props {
    record_id: any
    entity_id: any
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityById(state, props?.entity_id),
    record: getRecordById(state, props?.record_id),

    isPatching: isRecordPatching(state, getEntityById(state, props?.entity_id)?.name),

    entityType: getEntityTypeById(state, getRecordById(state, props?.record_id)?.type_id),
    entityTypes: getEntityTypesByEntityId(state, getEntityById(state, props?.entity_id)?.id),
    fields: getFieldsById(state),
    values: getValuesByEntityNameByEntityId(state, getEntityById(state, props?.entity_id)?.name, props.record_id)
});

const mapDispatch = {
    fetchEntityTypesByEntityId,
    fetchModules,
    fetchContactModules,

    updateVacancy,
    updateCandidate,
    updateBusinessUnit,
    updateContact,

    updateVacancyValues,
    updateCandidateValues,
    updateBusinessUnitValues,
    updateContactValues
};

type DetailedHeaderProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(DetailedHeader);